import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'

/**
 * Determines the receipt validation scenario from the Taggun result.
 *
 * Scenarios (from requirements):
 * 1. MISSING_DATA        – date.data = null OR grossValue.data missing, successful = false
 * 2. DUPLICATE           – similarityCheck.data not empty and similarityCheck.valid = false
 * 3. TAMPERED            – tamperDetection.isTampered = true
 * 4. DIFFERENT_PRODUCT   – matchedProductLineItems empty, successful = false
 * 5. TECHNICAL_ERROR     – HTTP error (non-200), no result object
 * 6. MISSING_RETAILER    – merchantName missing/not valid while successful = true
 * 7. GENERIC_FALLBACK    – any other failure (fallback)
 */
export const getScenario = (result, isTechnicalError) => {
    if (isTechnicalError) return 'TECHNICAL_ERROR'
    if (!result) return 'TECHNICAL_ERROR'

    // Duplicate receipt
    if (
        result.fraudDetection?.similarityCheck?.data?.length > 0 &&
        result.fraudDetection?.similarityCheck?.valid === false
    ) {
        return 'DUPLICATE'
    }

    // Fraud / tamper detection
    if (result.tamperDetection?.isTampered === true) return 'TAMPERED'

    // Missing purchase date
    if (result.date?.data === null || result.date?.data === undefined) {
        return 'MISSING_DATA'
    }

    // Different product (product not matched on receipt)
    if (
        result.successful === false &&
        Array.isArray(result.matchedProductLineItems) &&
        result.matchedProductLineItems.length === 0
    ) {
        return 'DIFFERENT_PRODUCT'
    }

    // Missing purchase value
    if (
        result.matchedProductLineItems?.[0]?.unitPrice === null ||
        result.matchedProductLineItems?.[0]?.unitPrice === undefined
    ) {
        return 'MISSING_DATA'
    }

    // Missing retailer: receipt is otherwise successful, but merchant cannot be trusted
    if (
        result.successful === true &&
        (result.merchantName?.data === null ||
            result.merchantName?.data === undefined ||
            result.merchantName?.valid === false)
    ) {
        return 'MISSING_RETAILER'
    }

    return 'GENERIC_FALLBACK'
}

const ReceiptValidationAlert = ({result, isTechnicalError, onShowManualFields, onRetry}) => {
    const scenario = getScenario(result, isTechnicalError)

    const renderMessage = () => {
        switch (scenario) {
            case 'MISSING_DATA':
                return (
                    <FormattedMessage
                        id="product_registration.receipt_missing_data"
                        defaultMessage="Our system couldn't automatically verify this receipt. The image might be unclear, or the store receipt may not list the exact model name. You can re-upload a clearer image, or proceed now to complete your registration. Please note that if you are applying for added benefits, your receipt status will remain pending and may require further verification later."
                    />
                )
            case 'DUPLICATE':
                return (
                    <FormattedMessage
                        id="product_registration.receipt_duplicate"
                        defaultMessage="This receipt appears to have already been uploaded to our system previously. Please ensure the image is clear and complete, then try again. Please note that if you proceed, the registration status will remain pending for verification and you will not be eligible for any additional benefit (should you be applying for one)."
                    />
                )
            case 'TAMPERED':
                return (
                    <FormattedMessage
                        id="product_registration.receipt_tampered"
                        defaultMessage="Our system has detected anomalies or alterations in the receipt. Please ensure the image is clear and complete, then try again. Please note that if you proceed, the registration status will remain pending for verification and you will not be eligible for any additional benefit (should you be applying for one)."
                    />
                )
            case 'DIFFERENT_PRODUCT':
                return (
                    <FormattedMessage
                        id="product_registration.receipt_missing_data"
                        defaultMessage="Our system couldn't automatically verify this receipt. The image might be unclear, or the store receipt may not list the exact model name. You can re-upload a clearer image, or proceed now to complete your registration. Please note that if you are applying for added benefits, your receipt status will remain pending and may require further verification later."
                    />
                )
            case 'MISSING_RETAILER':
                return (
                    <FormattedMessage
                        id="product_registration.receipt_missing_retailer"
                        defaultMessage={`We could not detect the store of your purchase. Please choose one from the following list or select "Other".`}
                    />
                )
            case 'TECHNICAL_ERROR':
                return (
                    <FormattedMessage
                        id="product_registration.receipt_technical_error"
                        defaultMessage="A technical error occurred while processing the receipt. Please try again later."
                    />
                )
            case 'GENERIC_FALLBACK':
            default:
                return (
                    <FormattedMessage
                        id="product_registration.receipt_generic_error"
                        defaultMessage="The receipt image is illegible. Please ensure the image is clear and complete, then try again. Please note that if you proceed with manual input, you will be able to complete the registration but the validation status will remain pending for verification and you will not be eligible for any additional benefit (should you be applying for one)."
                    />
                )
        }
    }

    const renderActions = () => {
        switch (scenario) {
            case 'MISSING_DATA':
            case 'DIFFERENT_PRODUCT':
                return (
                    <>
                        {onRetry && (
                            <Text
                                as="span"
                                textStyle="body-small"
                                color="text.tertiary"
                                cursor="pointer"
                                textDecoration="underline"
                                onClick={onRetry}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') onRetry()
                                }}
                                mr={4}
                            >
                                <FormattedMessage
                                    id="product_registration.receipt_upload_new_image"
                                    defaultMessage="Upload new image"
                                />
                            </Text>
                        )}
                        <Text
                            as="span"
                            display="block"
                            textStyle="body-small"
                            color="text.tertiary"
                            cursor="pointer"
                            textDecoration="underline"
                            onClick={onShowManualFields}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') onShowManualFields()
                            }}
                        >
                            <FormattedMessage
                                id="product_registration.receipt_proceed_registration"
                                defaultMessage="Proceed to complete registration"
                            />
                        </Text>
                    </>
                )
            case 'GENERIC_FALLBACK':
                return (
                    <>
                        {onRetry && (
                            <Text
                                as="span"
                                textStyle="body-small"
                                color="text.tertiary"
                                cursor="pointer"
                                textDecoration="underline"
                                onClick={onRetry}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') onRetry()
                                }}
                                mr={4}
                            >
                                <FormattedMessage
                                    id="product_registration.product_not_found.try_again_button"
                                    defaultMessage="Try Again"
                                />
                            </Text>
                        )}
                        <Text
                            as="span"
                            display="block"
                            textStyle="body-small"
                            color="text.tertiary"
                            cursor="pointer"
                            textDecoration="underline"
                            onClick={onShowManualFields}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') onShowManualFields()
                            }}
                        >
                            <FormattedMessage
                                id="product_registration.receipt_continue_manual"
                                defaultMessage="Continue with manual input"
                            />
                        </Text>
                    </>
                )
            case 'DUPLICATE':
            case 'TAMPERED':
                return (
                    <>
                        {onRetry && (
                            <Text
                                as="span"
                                textStyle="body-small"
                                color="text.tertiary"
                                cursor="pointer"
                                textDecoration="underline"
                                onClick={onRetry}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') onRetry()
                                }}
                                mr={4}
                            >
                                <FormattedMessage
                                    id="product_registration.product_not_found.try_again_button"
                                    defaultMessage="Try Again"
                                />
                            </Text>
                        )}
                        <Text
                            as="span"
                            display="block"
                            textStyle="body-small"
                            color="text.tertiary"
                            cursor="pointer"
                            textDecoration="underline"
                            onClick={onShowManualFields}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') onShowManualFields()
                            }}
                        >
                            <FormattedMessage
                                id="product_registration.receipt_continue"
                                defaultMessage="Continue"
                            />
                        </Text>
                    </>
                )
            case 'TECHNICAL_ERROR':
                return (
                    <>
                        {onRetry && (
                            <Text
                                as="span"
                                textStyle="body-small"
                                color="text.tertiary"
                                cursor="pointer"
                                textDecoration="underline"
                                onClick={onRetry}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') onRetry()
                                }}
                                mr={4}
                            >
                                <FormattedMessage
                                    id="product_registration.receipt_technical_error_retry"
                                    defaultMessage="Please try again later."
                                />
                            </Text>
                        )}
                        <Text
                            as="span"
                            display="block"
                            textStyle="body-small"
                            color="text.tertiary"
                            cursor="pointer"
                            textDecoration="underline"
                            onClick={onShowManualFields}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') onShowManualFields()
                            }}
                        >
                            <FormattedMessage
                                id="product_registration.receipt_continue_manual"
                                defaultMessage="Continue with manual input"
                            />
                        </Text>
                    </>
                )
            case 'MISSING_RETAILER':
                return null
            default:
                return null
        }
    }

    return (
        <Box
            bg="background.negative.muted"
            borderRadius="xl"
            border="1px solid"
            borderColor="border.neutral.highlight"
            boxShadow="0 0 0 1px var(--chakra-shadows-negative-lg, rgba(127, 18, 17, 0.24)), 0 4px 8px 0 var(--chakra-shadows-negative-xs, rgba(127, 18, 17, 0.08)), 0 6px 23px 0 var(--chakra-shadows-negative-xs, rgba(127, 18, 17, 0.08))"
            backdropFilter="blur(calc(var(--sm, 8px) / 2))"
            sx={{WebkitBackdropFilter: 'blur(calc(var(--sm, 8px) / 2))'}}
            p={4}
            mb={4}
            w={'100%'}
        >
            <Text textStyle="body-small" color="foreground.negative.default" mb={2}>
                {renderMessage()}
            </Text>
            {renderActions()}
        </Box>
    )
}

ReceiptValidationAlert.propTypes = {
    result: PropTypes.object,
    isTechnicalError: PropTypes.bool,
    onShowManualFields: PropTypes.func.isRequired,
    onRetry: PropTypes.func,
}

export default ReceiptValidationAlert
